import { useState, useMemo } from 'react';
import { Modal } from './Modal';
import { CATS, getCat, Avatar, fmt } from '../lib/helpers';

export function AddExpenseModal({ groups, people, defaultGroupId, onSave, onClose }) {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [cat, setCat] = useState('food');
  const [groupId, setGroupId] = useState(defaultGroupId || groups[0]?.id || '');
  const [paidBy, setPaidBy] = useState(people.find(p => p.name === 'You')?.id || people[0]?.id || '');
  const [splitType, setSplitType] = useState('equal');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  const group = useMemo(() => groups.find(g => g.id === +groupId), [groups, groupId]);
  const members = useMemo(() => group ? people.filter(p => group.members.includes(p.id)) : [], [group, people]);

  const splitPeople = useMemo(() => {
    if (splitType === 'equal') return members.map(p => p.id);
    if (splitType === 'payer') return [+paidBy];
    return members.map(p => p.id);
  }, [splitType, members, paidBy]);

  const share = splitPeople.length > 0 && amount ? (+amount / splitPeople.length) : 0;

  const submit = async () => {
    if (!name.trim() || !amount || !paidBy || !groupId || splitPeople.length === 0) {
      alert('Please fill all required fields.'); return;
    }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(), amount: +amount, paid_by: +paidBy,
        group_id: +groupId, category: cat, date,
        split: splitPeople.map(pid => ({ person_id: pid, share: +amount / splitPeople.length }))
      });
      onClose();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <Modal title="Add expense" onClose={onClose}>
      <div className="form-group">
        <label className="form-label">Description</label>
        <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Dinner, Rent…" />
      </div>
      <div className="form-group">
        <label className="form-label">Amount</label>
        <input className="form-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" min="0" step="0.01" />
      </div>
      <div className="form-group">
        <label className="form-label">Category</label>
        <div className="cat-grid">
          {CATS.map(c => (
            <button key={c.id} className={`cat-opt${cat === c.id ? ' active' : ''}`} onClick={() => setCat(c.id)}>
              <span style={{ fontSize: 18, display: 'block', marginBottom: 3 }}>{c.icon}</span>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Group</label>
        <select className="form-input" value={groupId} onChange={e => { setGroupId(e.target.value); setSplitType('equal'); }}>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Paid by</label>
        <select className="form-input" value={paidBy} onChange={e => setPaidBy(e.target.value)}>
          {members.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Date</label>
        <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Split</label>
        <div className="split-opts">
          {['equal', 'payer', 'custom'].map(t => (
            <button key={t} className={`split-opt${splitType === t ? ' active' : ''}`} onClick={() => setSplitType(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div>
          {members.map(p => {
            const inSplit = splitPeople.includes(p.id);
            return (
              <div className="member-split" key={p.id}>
                <div className="member-split-left">
                  <Avatar person={p} />
                  <span>{p.name}</span>
                </div>
                <span style={{ color: inSplit && share > 0 ? 'var(--green)' : 'var(--text-faint)', fontWeight: 500 }}>
                  {inSplit && share > 0 ? fmt(share) : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '9px' }} onClick={submit} disabled={saving}>
        {saving ? 'Saving…' : 'Add expense'}
      </button>
    </Modal>
  );
}
